import { jsxs, jsx } from "react/jsx-runtime";
import { FlexMixin } from "@zillow/constellation";
import { omit } from "lodash";
import PropTypes from "prop-types";
import "react";
import styled from "styled-components";
import { borderRadiusMixin } from "../../mixins/borderRadiusMixin.esm.js";
import { GroupMixin } from "../../mixins/components/GroupMixin.esm.js";
import { negativeMarginMixin } from "../../mixins/negativeMarginMixin.esm.js";
import customPropTypes from "../../utils/custom-prop-types.esm.js";
import withPreload from "../../utils/with-preload.esm.js";
import Picture from "../Picture/Picture.esm.js";
import Source from "../Source/Source.esm.js";
import GroupBackgroundImage from "./GroupBackgroundImage.esm.js";
import GroupBackgroundVideo from "./GroupBackgroundVideo.esm.js";
const GroupBackgroundImagePreload = withPreload(GroupBackgroundImage);
const GroupBackgroundVideoPreload = withPreload(GroupBackgroundVideo, {
  as: "video"
});
const TRANSPARENT_PIXEL_SOURCE = {
  src: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
  height: 0,
  width: 0,
  loading: "lazy"
};
const GroupContainer = styled.div.withConfig({
  displayName: "Group__GroupContainer",
  componentId: "sc-50qjnk-0"
})(["", " ", " ", " ", ""], GroupMixin, borderRadiusMixin, (props) => FlexMixin({
  // "visible" is a new prop. If not yet present, then default to
  // old way of always showing.
  display: props.visible ? {
    default: props.visible.default ? "flex" : "none",
    lg_lte: props.visible.lg_lte ? "flex" : "none",
    sm_lte: props.visible.sm_lte ? "flex" : "none"
  } : "flex",
  flex: 1,
  flexDirection: "column",
  ...props
}), negativeMarginMixin);
const Group = ({
  children,
  backgroundImage,
  backgroundImageVisible,
  backgroundVideo,
  backgroundVideoVisible,
  backgroundPosition,
  backgroundSize,
  backgroundVideoPosition,
  backgroundVideoSize,
  loading,
  backgroundImagePreloaded,
  isEditor,
  ...rest
}) => {
  const GroupBackgroundVideoComponent = isEditor ? GroupBackgroundVideo : GroupBackgroundVideoPreload;
  return /* @__PURE__ */ jsxs(GroupContainer, { ...rest, children: [
    renderBackgroundImage({
      backgroundImage,
      backgroundImageVisible,
      backgroundPosition,
      backgroundSize,
      loading,
      preloaded: backgroundImagePreloaded
    }),
    /* @__PURE__ */ jsx(
      GroupBackgroundVideoComponent,
      {
        source: backgroundVideo,
        visible: backgroundVideoVisible,
        size: backgroundVideoSize,
        position: backgroundVideoPosition,
        loading,
        isEditor
      }
    ),
    children
  ] });
};
function renderBackgroundImage({
  backgroundImage,
  backgroundImageVisible,
  backgroundPosition,
  backgroundSize,
  loading,
  preloaded
}) {
  var _a;
  const defaultSource = ((_a = backgroundImage == null ? void 0 : backgroundImage.default) == null ? void 0 : _a.src) ? backgroundImage.default : TRANSPARENT_PIXEL_SOURCE;
  const lgLteSource = (backgroundImageVisible == null ? void 0 : backgroundImageVisible.lg_lte) ? backgroundImage == null ? void 0 : backgroundImage.lg_lte : void 0;
  const smLteSource = (backgroundImageVisible == null ? void 0 : backgroundImageVisible.sm_lte) ? backgroundImage == null ? void 0 : backgroundImage.sm_lte : void 0;
  const hasBackgroundImage = [defaultSource, lgLteSource, smLteSource].filter(Boolean).some((source) => source !== TRANSPARENT_PIXEL_SOURCE);
  if (!hasBackgroundImage) {
    return null;
  }
  const GroupBackgroundImageComponent = preloaded ? GroupBackgroundImagePreload : GroupBackgroundImage;
  return /* @__PURE__ */ jsxs(Picture, { children: [
    smLteSource && /* @__PURE__ */ jsx(Source, { srcSet: smLteSource.src, media: "sm_lte", loading, ...omit(smLteSource, "src") }),
    lgLteSource && /* @__PURE__ */ jsx(Source, { srcSet: lgLteSource.src, media: "lg_lte", loading, ...omit(lgLteSource, "src") }),
    /* @__PURE__ */ jsx(GroupBackgroundImageComponent, { src: defaultSource.src, backgroundPosition, backgroundSize, backgroundImageVisible, loading, ...omit(defaultSource, "src") })
  ] });
}
Group.propTypes = {
  as: PropTypes.string,
  alignItems: customPropTypes.inlineMediaQuery(PropTypes.oneOf(["stretch", "center", "flex-start", "flex-end", "baseline"])),
  backgroundColor: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundImage: customPropTypes.inlineMediaQuery(PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number
  })),
  backgroundVideo: customPropTypes.inlineMediaQuery(PropTypes.shape({
    src: PropTypes.string
  })),
  backgroundImagePreloaded: PropTypes.bool,
  isEditor: PropTypes.bool,
  backgroundPosition: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundSize: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundVideoPosition: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundVideoSize: customPropTypes.inlineMediaQuery(PropTypes.string),
  backgroundImageVisible: customPropTypes.inlineMediaQuery(PropTypes.bool),
  backgroundVideoVisible: customPropTypes.inlineMediaQuery(PropTypes.bool),
  fontColor: customPropTypes.inlineMediaQuery(PropTypes.string),
  justifyContent: customPropTypes.inlineMediaQuery(PropTypes.oneOf(["flex-start", "flex-end", "center", "space-between", "space-around", "space-evenly", "initial", "inherit"])),
  maxWidth: customPropTypes.inlineMediaQuery(PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "full"])),
  minHeight: customPropTypes.inlineMediaQuery(PropTypes.oneOf(["none", "short", "tall", "fluid", "pixel"])),
  minHeightPixel: customPropTypes.inlineMediaQuery(PropTypes.string),
  padding: customPropTypes.inlineMediaQuery(PropTypes.shape({
    paddingTop: PropTypes.string,
    paddingRight: PropTypes.string,
    paddingLeft: PropTypes.string,
    paddingBottom: PropTypes.string
  })),
  borderRadius: customPropTypes.inlineMediaQuery(PropTypes.shape({
    borderRadiusTopLeft: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    borderRadiusTopRight: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    borderRadiusBottomLeft: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    borderRadiusBottomRight: PropTypes.oneOf([PropTypes.string, PropTypes.number])
  }))
};
Group.defaultProps = {
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "xl",
  minHeight: "none",
  minHeightPixel: "0px",
  isEditor: false
};
export {
  Group as default
};
